import { defineStore } from 'pinia';
import { markRaw } from 'vue';
import { ServicesBuilder } from '../utils/setup';
import { IPrinter, IPrinterService, IPrintTemplateService, PrinterType, IPrintTemplate, PrintJobService, IPrintJobService, IPrintJob, ISectionItem, SystemError, TemplateData, Config, RenderPrintTemplateOptions, PrintJob, Codepages, OrderFlow, IMenu } from '@posx/core';
import { PrintTemplateService, Printer, PrinterService } from '@posx/core';
import secureStorage from '@xpos/utils/src/secure.storage.utils';
import { createRequest } from '@/utils/http';
import { TOrderCodeRequest, TOrderCodeResponse } from '@/types/order-code';
import { useConfigStore } from './config';
import { IPayInOutData, PAY_IN_OUT_PRINT_TEMPLATE } from '@/types/pay-in-out.type';
import dayjs from 'dayjs';
import { logInfo, logLiveQueryError, isLiveQueryCursorError } from '@/utils/logger';
import { captureException } from '@sentry/vue';
import { Subscription } from 'dexie';

export const usePrinterStore = defineStore('printer', {
  state: () => ({
    list: [] as IPrinter[],
    totalPage: 0,
    printTemplates: [] as IPrintTemplate[],
    printTemplateTotalPage: 0,
    service: null as IPrinterService | null,
    PrintTemplateService : null as IPrintTemplateService | null,
    printJobService: null as IPrintJobService | null,
    _subscription: null as Subscription | null,
    _templateSubscription: null as Subscription | null,
  }),
  actions: {
    async getTerminalPrinter(type: PrinterType): Promise<IPrinter | null> {
      const currentDeviceUid = secureStorage.getItem('device_uid');
      if (!currentDeviceUid) return null;
      const service = await this.getService();
      const printers = await service.getMany({ type }, { pageSize: 1000 });
      return printers.list.find(p => p.terminal_device_uids?.includes(currentDeviceUid)) || null;
    },
    async getService(newInstance: boolean = false): Promise<IPrinterService> {
      if (this.service && !newInstance) return this.service;
      else {
        // service singleton is method-only, skip deep proxy
        this.service = markRaw(await ServicesBuilder.getInstance().getService<IPrinterService>(PrinterService));
        return this.service;
      }
    },
    async getPrintJobService(): Promise<IPrintJobService> {
      if (this.printJobService) return this.printJobService;
      else {
        // service singleton is method-only, skip deep proxy
        this.printJobService = markRaw(await ServicesBuilder.getInstance().getService<IPrintJobService>(PrintJobService));
        return this.printJobService;
      }
    },
    async load() {
      if (this._subscription) return;
      const service = await this.getService();
      const sub: Subscription = service
        .liveQuery(() => service.getMany({}, { pageSize: 1000 }))
        .subscribe({
          next: async (resultPromise) => {
            const result = await resultPromise;
            this.list = result.list;
            this.totalPage = result.count;
          },
          // dexie error path never sets closed=true; drop the dead listener before allowing re-subscribe
          error: (err) => { logLiveQueryError(err, { store: 'printer', query: 'load' }); if (isLiveQueryCursorError(err) && this._subscription === sub) { sub.unsubscribe(); this._subscription = null; } },
        });
      this._subscription = markRaw(sub);
    },
    async loadPrintTemplates() {
      if (this._templateSubscription) return;
      const service = await this.getPrintTemplateService();
      const templateSub: Subscription = service.liveQuery(() => service.getMany({}, { pageSize: 1000 })).subscribe({
        next: async (resultPromise) => {
          const result = await resultPromise;
          this.printTemplates = result.list;
          this.printTemplateTotalPage = result.count;
        },
        // dexie error path never sets closed=true; drop the dead listener before allowing re-subscribe
        error: (err) => { logLiveQueryError(err, { store: 'printer', query: 'loadPrintTemplates' }); if (isLiveQueryCursorError(err) && this._templateSubscription === templateSub) { templateSub.unsubscribe(); this._templateSubscription = null; } },
      });
      this._templateSubscription = markRaw(templateSub);
    },
    stopLiveQuery() {
      this._subscription?.unsubscribe();
      this._subscription = null;
      this._templateSubscription?.unsubscribe();
      this._templateSubscription = null;
    },
    async getPrintTemplateService(): Promise<IPrintTemplateService> {
      if (this.PrintTemplateService) return this.PrintTemplateService;
      else {
        // service singleton is method-only, skip deep proxy
        this.PrintTemplateService = markRaw(await ServicesBuilder.getInstance().getService<IPrintTemplateService>(PrintTemplateService));
        return this.PrintTemplateService;
      }
    },
    async savePrintJob(payload: IPrintJob) {
      const service = await this.getPrintJobService();
      await service.saveOne(payload);
    },
    async hardDeleteAndUpdatePrintJob(payload: IPrintJob) {
      const service = await this.getPrintJobService();
      await service.updateOne(payload.id_in_server, payload);
      await service.hardDeleteOne(payload);
    },

    async getMany(params: Partial<IPrinter>) {
      const service = await ServicesBuilder.getInstance().getService<PrinterService>(PrinterService);
      return service?.getMany(params, { pageSize: 1000 });
    },
    async update(payload: Printer) {
      const service = await ServicesBuilder.getInstance().getService<PrinterService>(PrinterService);
      service?.updateOne(payload.id_in_server, payload);
    },
    async getTemplateList(params: Partial<IPrintTemplate>) {
      const service = await ServicesBuilder.getInstance().getService<IPrintTemplateService>(PrintTemplateService);
      return service?.getMany(params, { pageSize: 1000 });
    },
    async getUnpairedNode() {
      const service = await ServicesBuilder.getInstance().getService<PrinterService>(PrinterService);
      return service?.getUnpairedNode();
    },
    async pairNode(uid: string) {
      const service = await ServicesBuilder.getInstance().getService<PrinterService>(PrinterService);
      return service?.pairNode(uid);
    },
    /** Initialize print templates with auto-retry, skip on failure to avoid blocking sync */
    async initPrintTemplates() {
      const service = await this.getPrintTemplateService();
      for (let i = 0; i < 3; i++) {
        try { return await service.initialize(); }
        catch (e) {
          if (i === 2) { captureException(e); return; }
          await new Promise(r => setTimeout(r, 1000));
        }
      }
    },
    async searchPrinters() {
      const service = await ServicesBuilder.getInstance().getService<PrinterService>(PrinterService);
      return service?.searchPrinters();
    },
    async createQrcodePrintJob(sectionItem: ISectionItem, orderFlow: OrderFlow, menu?: IMenu) {
        const merchantUid = secureStorage.getItem('merchant_uid') || '';
        if(!merchantUid) throw new SystemError('merchant uid is not found')
        const request = createRequest(import.meta.env.VITE_LITE_BASE_URL);
        const requestData: TOrderCodeRequest = {
            section_item_uid: sectionItem.uid,
            section_item_name: sectionItem.name,
            order_flow: orderFlow
        };
        // Add menu information if provided
        if (menu) {
            requestData.menu_uid = menu.uid;
            requestData.menu_name = menu.name;
        }
        // Menu-level expiry overrides global, 0 means inherit from global
        const globalExpiryMinutes = useConfigStore().appConfig?.config?.online_order?.dynamic_order_qr?.expiry_in_minutes ?? 0;
        const menuExpiryMinutes = menu?.online_order_expiry_in_minutes ?? 0;
        const expiryMinutes = menuExpiryMinutes > 0 ? menuExpiryMinutes : globalExpiryMinutes;
        if (expiryMinutes > 0) requestData.expiry_in_minutes = expiryMinutes;
        const response = await request.put<TOrderCodeResponse>(`api/v5/merchants/${merchantUid}/dynamic_order_codes/dynamic_order_code`, requestData);
        if (!response.data?.data?.order_url) throw new SystemError('scan_order_service_not_configured');
        const service = await this.getService();
        let printer = await this.getTerminalPrinter(PrinterType.QrCodePrinter);
        if (!printer && sectionItem?.qr_printer_uid) printer = await service.getOne(sectionItem.qr_printer_uid);
        if (!printer) printer = await service.getDefaultPrinterByType(PrinterType.QrCodePrinter);
        if (!printer) throw new SystemError('scan_order_service_not_configured');
        const printTemplateService = await this.getPrintTemplateService();
        const template = await printTemplateService.getOneByParams({ uid: printer.print_template_uid });
        if(!template) throw new SystemError('scan_order_service_not_configured');
        const configStore = useConfigStore();
        const appConfig = configStore.appConfig;
        const data = new TemplateData();
        data.config = appConfig?.config || new Config();
        data.section_item = sectionItem;
        data.meta = {
            ...data.meta || {},
            online_order_url: response.data.data.order_url,
            menu_name: menu?.name ?? '',
            menu_name_translations: menu?.name_translations ?? {},
            menu_expiry_in_minutes: expiryMinutes
        }
        data.printer_name = printer.name;
        await this.renderAndQueuePrintJob(printer, template, data);
    },
    async createPayInPayoutPrintJob(payInPayoutData: IPayInOutData) {
      const configStore = useConfigStore();
      const appConfig = configStore.appConfig;
      if(appConfig && appConfig.config.till.slip == false) {
        return;
      }

      const service = await this.getService();
      const printer = await service.getDefaultPrinterByType(PrinterType.ReceiptPrinter);
      if(!printer) throw new SystemError('Receipt printer is not found');

      const printTemplateService = await this.getPrintTemplateService();
      // Prefer the EventPrinter template rendered whole (branches on event_type); old shops without one keep the hardcoded slip
      let template = await printTemplateService.getOneByParams({ printer_type: PrinterType.EventPrinter });
      if (!template) {
        template = await printTemplateService.getOneByParams({ uid: printer.print_template_uid });
        if(!template) throw new SystemError('Receipt template is not found');
        template.header = '{}';
        template.footer = '{}';
        template.body = JSON.stringify(PAY_IN_OUT_PRINT_TEMPLATE);
      }

      const data = new TemplateData();
      Object.assign(data, {
          event_type: `pay_${payInPayoutData.type.toLowerCase()}`,
          type: payInPayoutData.type,
          amount: payInPayoutData.amount,
          reason: payInPayoutData.reason,
          payment_method_name: payInPayoutData.payment_method_name,
          employee_name: payInPayoutData.employee_name,
          timestamp: payInPayoutData.timestamp || new Date(),
          humanized_print_date: dayjs(payInPayoutData.timestamp).format('YYYY-MM-DD HH:mm:ss')
      });

      await this.renderAndQueuePrintJob(printer, template, data);
    },
    async createTableSwitchPrintJob(fromTable: string, toTable: string, employeeName?: string) {
      // Config gating must be first per incident-470
      if (!useConfigStore().appConfig?.config?.event_print?.table_switch_slip) return;
      const service = await this.getService();
      // One switch slip to every configured EventPrinter
      const { list: printers } = await service.getMany({ type: PrinterType.EventPrinter }, { pageSize: 1000 });
      if (!printers.length) return logInfo('table switch print skipped: no event printer');
      const printTemplateService = await this.getPrintTemplateService();
      // Always render the EventPrinter template, decoupled from each printer's assigned template
      const template = await printTemplateService.getOneByParams({ printer_type: PrinterType.EventPrinter });
      if (!template) return logInfo('table switch print skipped: no template');
      const data = new TemplateData();
      Object.assign(data, { event_type: 'table_switch', from_table: fromTable, to_table: toTable, employee_name: employeeName, humanized_print_date: dayjs().format('YYYY-MM-DD HH:mm:ss') });
      for (const printer of printers) await this.renderAndQueuePrintJob(printer, template, data);
    },
    // Shared tail of every print flow: render template for this printer, then enqueue a single-copy job
    async renderAndQueuePrintJob(printer: IPrinter, template: IPrintTemplate, data: TemplateData): Promise<IPrintJob> {
      const printTemplateService = await this.getPrintTemplateService();
      const printCommand = await printTemplateService.renderPrintTemplateByData(template, data, { ...new RenderPrintTemplateOptions(), printer_name: printer.name });
      printCommand.codepage = template.codepage || Codepages.SimplifiedChinese;
      const printJob = new PrintJob(printer, printCommand, false, 1);
      printJob.printer_type = printer.type;
      printJob.connection_type = printer.connection_type;
      await (await this.getPrintJobService()).addOne(printJob);
      return printJob;
    }
  },
});
